import { _ } from "../../../polyglot/polyglot.js";
import { stacker } from "../../../elements/index.js";
import AbstractImporter from "../AbstractImporter.js";
import SieImportIntegration from "../../../../models/integration/SieImportIntegration.js";
import SieImportService from "./SieImportService.js";
import UploadFileStep from "../UploadFileStep.js";

/**
 * Wizard to import a company from a SIE file.
 */
export default class SieImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		this._importService = new SieImportService();
	}

	routeName() {
		return "sie";
	}

	_wantsBudgetPicker() {
		return true;
	}

	_wizardSteps() {
		return [this._uploadFileStep()];
	}

	_uploadFileStep() {
		return new UploadFileStep({
			stepText: _(
				"Click on 'Choose a SIE-file' and browse on your computer or file-server to choose a SIE4-file that you want to import.",
			),
			uploadButtonLabel: _("Choose a SIE-file"),
			onUpload: (file) => this._importService.uploadFile(file),
			submitCallback: this._getSieData.bind(this),
		});
	}

	async _getSieData({ onDone, step }) {
		try {
			const data = await this._importService.getSieImportedData({
				companyId: this._getCompanyId(),
				agencyId: this._context.company.getOwnerIdentifier() || null,
			});

			if (data.hasErrors) {
				data.errors.forEach((error) =>
					this._addErrorToStep({ error, step }),
				);
			}

			this._updateContextWithImportData(data);
		} catch (error) {
			this._addErrorToStep({ error, step });
		} finally {
			onDone();
		}
	}

	_updateContextWithImportData({
		importCompanyInfo,
		chartOfAccounts,
		budgets,
	}) {
		this._context.importData = {
			importCompanyInfo,
			chartOfAccounts,
			budgets: budgets || [],
		};

		this._context.chartsOfAccounts ||= chartOfAccounts;
		this._context.budgets = budgets;
	}

	_addErrorToStep({ error, step }) {
		step.addSubmitError(this._errorToMessage(error));
	}

	_errorToMessage(error) {
		if (error.type === "BadSyntax")
			return this._badSyntaxErrorToMessage(error);

		const message = error?.responseJSON?.message;
		return message || _("Failed to load data");
	}

	_badSyntaxErrorToMessage(error) {
		return stacker(
			{ direction: "vertical", align: "left" },
			(html) => html.b(error.title),
			error.explanation,
			(html) => html.i(_("Line: {line}", { line: error.line || "-" })),
			(html) =>
				html.i(
					_("Line number: {lineNumber}", {
						lineNumber: error.lineNo || "-",
					}),
				),
			(html) =>
				html.i(
					_("Line position: {linePosition}", {
						linePosition: error.linePos || "-",
					}),
				),
		);
	}
}

SieImporter.integrationType = SieImportIntegration.getType();
